import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { StepContent } from '@/components/qualif/StepContent';
import {
  ArrowLeft, ArrowRight, CheckCircle2, Loader2, Sparkles,
  Clock, ShieldCheck, Home, Wrench, Mail, CalendarCheck,
  Phone, AlertCircle, X,
} from 'lucide-react';
import { estimateAid, categoryFields, trackEvent, trackConversion, getTrafficSource } from '@/data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  { label: 'Contact', Icon: Phone },
  { label: 'Logement', Icon: Home },
  { label: 'Projet', Icon: Wrench },
  { label: 'Qualification', Icon: Mail },
];

const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div role="alert" className={`fixed top-4 left-1/2 -translate-x-1/2 z-[100] flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium toast-enter ${
      type === 'success' ? 'bg-[#0f766e] text-white' : 'bg-red-500 text-white'
    }`}>
      {type === 'success' ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
      {message}
      <button onClick={onClose} className="ml-2 hover:opacity-70" aria-label="Fermer"><X className="h-3.5 w-3.5" /></button>
    </div>
  );
};

export const QualifModal = ({ open, onClose, category }) => {
  const [phase, setPhase] = useState('welcome');
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [leadId, setLeadId] = useState(null);
  const [score, setScore] = useState(null);
  const [animScore, setAnimScore] = useState(0);
  const [aid, setAid] = useState(null);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);

  const onChange = useCallback((key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  useEffect(() => {
    if (phase !== 'result' || !score) return;
    let n = 0;
    const iv = setInterval(() => { n += 1; setAnimScore(n); if (n >= score) clearInterval(iv); }, 18);
    return () => clearInterval(iv);
  }, [phase, score]);

  const canProceed = () => {
    switch (step) {
      case 0:
        return !!formData.firstName && !!formData.lastName &&
          formData.phone?.replace(/\s/g, '').length >= 10 &&
          !!formData.status && formData.department?.length >= 2;
      case 1:
        if (formData.housingType === 'appartement') return false;
        return !!formData.housingType && !!formData.heatingType && !!formData.livingSurface;
      case 2: {
        const fields = categoryFields[category?.id] || [];
        return fields.every((f) => !!formData[f.id]) && !!formData.dpe && !!formData.timeline && !!formData.hasQuote;
      }
      case 3:
        return !!formData.revenueRange && formData.email?.includes('@') && !!formData.callbackTime;
      default:
        return false;
    }
  };

  const saveStep = async (stepNum) => {
    setSaving(true);
    try {
      if (stepNum === 0) {
        // Step 1: Create lead
        const trafficSource = getTrafficSource();
        const resp = await axios.post(`${API}/qualifications/step1`, {
          category: category.id,
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone: formData.phone,
          status: formData.status,
          department: formData.department,
          traffic_source: trafficSource,
        });
        setLeadId(resp.data.id);
        setToast({ message: 'Contact enregistré !', type: 'success' });

        // CONVERSION: generate_lead (lead captured in DB)
        trackConversion('generate_lead', {
          category: category.id,
          lead_id: resp.data.id,
          value: 1,
          currency: 'EUR',
        });
      } else if (leadId) {
        // Steps 2-4: Update lead
        const catFields = categoryFields[category?.id] || [];
        const categoryDetails = {};
        catFields.forEach((f) => { if (formData[f.id]) categoryDetails[f.id] = formData[f.id]; });

        const resp = await axios.patch(`${API}/qualifications/${leadId}/step/${stepNum + 1}`, {
          address: formData.address || '',
          city: formData.city || '',
          postal: formData.postal || '',
          housing_type: formData.housingType || '',
          heating_type: formData.heatingType || '',
          living_surface: formData.livingSurface || '',
          dpe: formData.dpe || '',
          category_details: categoryDetails,
          timeline: formData.timeline || '',
          has_quote: formData.hasQuote || '',
          construction_year: formData.constructionYear || '',
          revenue_range: formData.revenueRange || '',
          email: formData.email || '',
          callback_time: formData.callbackTime || '',
        });

        if (stepNum === 1) {
          // Step 2 complete: housing info
          trackConversion('housing_info_complete', {
            category: category.id,
            lead_id: leadId,
            housing_type: formData.housingType,
            heating_type: formData.heatingType,
            value: 5,
            currency: 'EUR',
          });
        }

        if (stepNum === 2) {
          // Step 3 complete: project details
          trackConversion('project_details_complete', {
            category: category.id,
            lead_id: leadId,
            dpe: formData.dpe,
            timeline: formData.timeline,
            has_quote: formData.hasQuote,
            value: 10,
            currency: 'EUR',
          });
        }

        if (stepNum === 3) {
          // Step 4 complete: full qualification
          setScore(resp.data.eligibility_score);
          setAid(resp.data.estimated_aid);
          setToast({ message: 'Profil complet ! Un conseiller vous rappelle sous 24h.', type: 'success' });

          trackConversion('qualify_complete', {
            category: category.id,
            lead_id: leadId,
            eligibility_score: resp.data.eligibility_score,
            estimated_aid_min: resp.data.estimated_aid?.min,
            estimated_aid_max: resp.data.estimated_aid?.max,
            value: resp.data.estimated_aid?.min || 0,
            currency: 'EUR',
          });
        }
      }
    } catch (err) {
      console.warn('Save error:', err);
      if (stepNum === 3) {
        // Fallback for final step
        const s = 75;
        setScore(s);
        setAid(estimateAid(formData, category?.id));
      }
    }
    setSaving(false);
  };

  const handleNext = async () => {
    if (step < 3) {
      await saveStep(step);
      setStep((s) => s + 1);
      trackEvent('qualif_step', { category: category?.id, step: step + 2, step_name: STEPS[step + 1]?.label });
    } else {
      setPhase('loading');
      await saveStep(3);
      setPhase('result');
    }
  };

  const reset = () => {
    setPhase('welcome'); setStep(0); setFormData({});
    setLeadId(null); setScore(null); setAnimScore(0);
    setAid(null); setToast(null); setSaving(false);
    onClose();
  };

  if (!category) return null;
  const progress = ((step + 1) / STEPS.length) * 100;
  const liveAid = estimateAid(formData, category.id);

  const WelcomeView = () => (
    <div className="p-6 sm:p-8 text-center">
      <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl ${category.iconBg} mb-5`} aria-hidden="true">
        <span className={`text-3xl ${category.iconColor} font-bold`}>{category.subtitle}</span>
      </div>
      <DialogTitle className="text-xl font-bold text-[#1f2937] mb-1">Qualification {category.title}</DialogTitle>
      <DialogDescription className="text-sm text-gray-500 mb-6">Découvrez vos aides MaPrimeRénov' 2026 en 4 étapes</DialogDescription>
      <div className="flex items-center justify-center gap-2 mb-8" aria-label="Étapes">
        {STEPS.map((s, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center">
              <s.Icon className="h-4 w-4 text-[#0f766e]" />
            </div>
            <span className="text-xs font-medium text-gray-500 hidden sm:inline">{s.label}</span>
            {i < STEPS.length - 1 && <div className="w-4 h-px bg-gray-300 mx-1" aria-hidden="true" />}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-center gap-6 text-xs text-gray-400 mb-8">
        <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> 2 min</span>
        <span className="flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Gratuit</span>
        <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Sans engagement</span>
      </div>
      <Button data-testid="qualif-start-btn" onClick={() => { setPhase('form'); }}
        className="w-full h-14 text-base font-semibold rounded-xl bg-[#0f766e] hover:bg-[#115e59] text-white shadow-lg animate-pulse-subtle">
        C'est parti ! <ArrowRight className="ml-2 h-5 w-5" />
      </Button>
    </div>
  );

  const FormView = () => (
    <>
      <div className="bg-[#0f766e] px-5 pt-5 pb-4">
        <DialogTitle className="sr-only">Qualification {category.subtitle}</DialogTitle>
        <DialogDescription className="sr-only">Étape {step + 1} sur {STEPS.length} : {STEPS[step].label}</DialogDescription>
        <nav aria-label="Progression" className="flex items-center gap-1 mb-3">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-center gap-1">
              <div aria-current={i === step ? 'step' : undefined}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${
                  i < step ? 'bg-teal-300 text-[#0f766e]' : i === step ? 'bg-white text-[#0f766e]' : 'bg-teal-800/40 text-teal-300'
                }`}>
                {i < step ? <CheckCircle2 className="h-4 w-4" /> : <s.Icon className="h-3.5 w-3.5" />}
              </div>
              <span className={`text-xs font-medium hidden sm:inline ${i === step ? 'text-white' : 'text-teal-300/60'}`}>{s.label}</span>
              {i < STEPS.length - 1 && <div className="w-3 h-px bg-teal-400/30 mx-0.5" aria-hidden="true" />}
            </div>
          ))}
        </nav>
        <Progress value={progress} className="h-1.5 bg-teal-900/40 [&>div]:bg-teal-200 [&>div]:transition-transform [&>div]:duration-500" />
        <p className="text-teal-200 text-xs font-medium mt-2 sm:hidden">Étape {step + 1}/{STEPS.length} — {STEPS[step].label}</p>
      </div>
      <div className="px-5 py-5 flex-1 min-h-0 overflow-y-auto" key={step} style={{ animation: 'slideIn 0.3s ease-out' }}>
        <StepContent step={step} formData={formData} onChange={onChange} category={category} />
      </div>
      {liveAid && step >= 2 && (
        <div data-testid="live-aid-estimate" className="mx-5 mb-3 bg-teal-50 border border-teal-200 rounded-xl px-4 py-2.5 flex items-center justify-between" aria-live="polite">
          <span className="text-xs font-medium text-[#0f766e]">Estimation aide</span>
          <span className="text-sm font-bold text-[#0f766e]">{liveAid.min.toLocaleString('fr-FR')} – {liveAid.max.toLocaleString('fr-FR')} {liveAid.unit}</span>
        </div>
      )}
      <div className="px-5 pb-5 flex justify-between items-center">
        <Button data-testid="qualif-back-btn" variant="ghost"
          onClick={() => (step > 0 ? setStep((s) => s - 1) : setPhase('welcome'))}
          className="h-11 text-gray-400 hover:text-gray-600">
          <ArrowLeft className="mr-1 h-4 w-4" /> Retour
        </Button>
        <Button data-testid="qualif-next-btn" onClick={handleNext} disabled={!canProceed() || saving}
          className="h-11 rounded-xl bg-[#0f766e] hover:bg-[#115e59] text-white px-6 disabled:opacity-40">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
          {step < 3 ? <><span>Suivant</span><ArrowRight className="ml-1 h-4 w-4" /></> : 'Voir mes résultats'}
        </Button>
      </div>
    </>
  );

  const LoadingView = () => (
    <div className="p-10 text-center" role="status">
      <DialogTitle className="sr-only">Analyse en cours</DialogTitle>
      <DialogDescription className="sr-only">Calcul de votre éligibilité</DialogDescription>
      <Loader2 className="h-12 w-12 text-[#0f766e] animate-spin mx-auto mb-6" />
      <h3 className="text-lg font-bold text-[#1f2937] mb-2">Analyse de votre dossier…</h3>
      <div className="mt-6 space-y-2 max-w-xs mx-auto">
        {['Vérification du profil', 'Analyse du logement', 'Calcul des aides'].map((t, i) => (
          <div key={i} className="flex items-center gap-2 text-sm text-gray-500"
            style={{ animation: `slideIn 0.4s ease-out ${i * 0.5}s both` }}>
            <CheckCircle2 className="h-4 w-4 text-[#0f766e]" />{t}
          </div>
        ))}
      </div>
    </div>
  );

  const ResultView = () => (
    <div className="p-6 sm:p-8 text-center" aria-live="polite">
      <DialogTitle className="sr-only">Résultat</DialogTitle>
      <DialogDescription className="sr-only">Score {score}%</DialogDescription>
      <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 rounded-full mb-4"><Sparkles className="h-8 w-8 text-[#0f766e]" /></div>
      <h3 className="text-xl font-bold text-[#1f2937] mb-1">Votre résultat</h3>
      <p className="text-sm text-gray-400 mb-5">Qualification {category.subtitle} — MaPrimeRénov' 2026</p>
      <div data-testid="eligibility-score" className="mb-5" style={{ animation: 'scaleIn 0.5s ease-out' }}>
        <div className="relative w-32 h-32 mx-auto">
          <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
            <circle cx="60" cy="60" r="52" fill="none" stroke="#e5e7eb" strokeWidth="8" />
            <circle cx="60" cy="60" r="52" fill="none" stroke="#0f766e" strokeWidth="8" strokeLinecap="round"
              strokeDasharray={`${(animScore / 100) * 327} 327`} className="transition-all duration-100" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-3xl font-extrabold text-[#0f766e]">{animScore}%</span>
            <span className="text-[0.6rem] text-gray-400 mt-0.5">éligibilité</span>
          </div>
        </div>
      </div>
      {aid && (
        <div data-testid="aid-estimate" className="bg-teal-50 border border-teal-200 rounded-xl p-4 mb-5">
          <p className="text-sm font-semibold text-[#0f766e] mb-1">Estimation de vos aides</p>
          <p className="text-2xl font-extrabold text-[#115e59]">{aid.min.toLocaleString('fr-FR')} – {aid.max.toLocaleString('fr-FR')} €</p>
          {aid.perUnit && <p className="text-xs text-teal-600 mt-1">{aid.perUnit}</p>}
        </div>
      )}
      {score >= 70 ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-5 text-left">
          <p className="text-sm font-semibold text-emerald-800 mb-1">Excellente nouvelle !</p>
          <p className="text-xs text-emerald-700">Un conseiller certifié RGE vous contactera sous 24h.</p>
        </div>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5 text-left">
          <p className="text-sm font-semibold text-amber-800 mb-1">Éligibilité partielle</p>
          <p className="text-xs text-amber-700">Un conseiller analysera votre situation pour maximiser vos aides.</p>
        </div>
      )}
      <div className="space-y-2 text-left mb-6">
        {[{ icon: CalendarCheck, text: 'Un conseiller vous rappelle sous 24h' },
          { icon: CheckCircle2, text: 'Visite technique gratuite à domicile' },
          { icon: Sparkles, text: "Dossier MaPrimeRénov' déposé pour vous" },
        ].map(({ icon: Ic, text }, i) => (
          <div key={i} className="flex items-center gap-2.5 text-sm text-gray-600"><Ic className="h-4 w-4 text-[#0f766e] flex-shrink-0" />{text}</div>
        ))}
      </div>
      <Button data-testid="qualif-close-btn" onClick={reset}
        className="w-full rounded-xl bg-[#0f766e] hover:bg-[#115e59] text-white h-12 text-base font-semibold">Fermer</Button>
    </div>
  );

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <Dialog open={open} onOpenChange={reset}>
        <DialogContent className="w-[calc(100%-1.5rem)] sm:max-w-[460px] max-h-[90dvh] sm:max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden rounded-2xl border-0 [&>button]:text-white [&>button]:hover:text-teal-200 [&>button]:z-10">
          {phase === 'welcome' && WelcomeView()}
          {phase === 'form' && FormView()}
          {phase === 'loading' && LoadingView()}
          {phase === 'result' && ResultView()}
        </DialogContent>
      </Dialog>
    </>
  );
};
